import React from 'react';
import { Route, Switch } from 'react-router-dom';
import { Routes } from 'src/shared/Routes';
import { TrackedAuthRouteWithErrorBoundary } from 'src/tracking/authenticated-route';
import { TrackedRouteWithErrorBoundary } from 'src/tracking/tracked-route';
import { lazyRetry } from 'src/util/retry';
import { tableRunBrandRoutes } from './tableRunBrandRoutes';

const ConsumerDeliveryAddresses = lazyRetry(
  () => import('src/pages/consumer/account/delivery-addresses/ConsumerDeliveryAddresses')
);
const ConsumerLogin = lazyRetry(() => import('src/auth/page/LoginPage'));
const ConsumerResetChangePassword = lazyRetry(() => import('src/auth/page/ConsumerResetChangePassword'));
const ConsumerPrivacyPolicy = lazyRetry(() => import('src/pages/consumer/static/ConsumerPrivacyPolicy'));
const ConsumerTableRunOrders = lazyRetry(() => import('src/pages/consumer/account/tablerun-orders'));
const ConsumerTableRunSubscriptions = lazyRetry(() => import('src/pages/consumer/account/tablerun-subscriptions'));
const ConsumerTos = lazyRetry(() => import('src/pages/consumer/static/ConsumerTos'));
const TableRunMarketingPage = lazyRetry(() => import('./TableRunMarketingPage'));
const TableRunNotFoundPage = lazyRetry(() => import('./TableRunNotFoundPage'));

/**
 * Everything reachable on a tablerun.com host. The account routes are auth-gated here but not on
 * shef.com, where the account section has its own conventions, so they are registered per host
 * rather than shared through {@link tableRunBrandRoutes}.
 *
 * The legal routes must stay registered here: they are the targets of the signup consent
 * checkbox, and an unregistered path would otherwise fall through to the not-found page rather
 * than serving the terms a customer is being asked to accept.
 *
 * The marketing page is `exact` and a not-found page takes the final slot, so an unknown path
 * says so instead of rendering marketing copy. Note this is a rendered fallback, not an HTTP 404 —
 * CloudFront and nginx serve `index.html` for every path, so the status code is still 200.
 */
export const TableRunConsumerRouter: React.FC = () => (
  <Switch>
    <TrackedRouteWithErrorBoundary path={Routes.CONSUMER_LOGIN} component={ConsumerLogin} />
    <TrackedRouteWithErrorBoundary path={Routes.CONSUMER_REGISTER} component={ConsumerLogin} />
    <TrackedRouteWithErrorBoundary
      path={Routes.CONSUMER_RESET_CHANGE_PASSWORD}
      component={ConsumerResetChangePassword}
    />
    <TrackedRouteWithErrorBoundary path={Routes.CONSUMER_PRIVACY_POLICY} component={ConsumerPrivacyPolicy} />
    <TrackedRouteWithErrorBoundary path={Routes.CONSUMER_TOS} component={ConsumerTos} />
    {tableRunBrandRoutes()}
    <TrackedAuthRouteWithErrorBoundary
      path={Routes.CONSUMER_ACCOUNT_DELIVERY_ADDRESSES}
      exact
      component={ConsumerDeliveryAddresses}
      loginHeader='Log in to manage your delivery addresses'
    />
    <TrackedAuthRouteWithErrorBoundary
      path={Routes.CONSUMER_ACCOUNT_ORDERS}
      exact
      component={ConsumerTableRunOrders}
      loginHeader='Log in to see your orders'
    />
    <TrackedAuthRouteWithErrorBoundary
      path={Routes.CONSUMER_ACCOUNT_TABLERUN_SUBSCRIPTIONS}
      exact
      component={ConsumerTableRunSubscriptions}
      loginHeader='Log in to manage your subscriptions'
    />
    <Route path='/' exact component={TableRunMarketingPage} />
    <Route component={TableRunNotFoundPage} />
  </Switch>
);
